import { Component, OnInit, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { forwardRef, Inject, NgZone } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { pick } from 'lodash-es';
import { MY_MONACO_EDITOR_CONFIG, MyMonacoEditorConfig } from './editorconfig';
import { CodeEditorEventService } from './my-monaco-editor-services/monaco-editor.event.service';
import { CODE_EDITOR_EVENTS } from './my-monaco-editor-services/events';
import { ExpressionEventService } from '../utils/expression-event-service';
import { CommonUtils } from '../utils/CommonUtils';

declare const monaco: any;

let loadedMonaco: boolean = false;
let loadPromise: Promise<void>;

// 自定义输入控件:1.封装ControlValueAccessor
// https://code-examples.net/zh-CN/q/2154761
export const CODE_EDITOR_INPUT_VALUE_ACCESSOR: any = {
  // https://blog.csdn.net/wangdan_2013/article/details/81314959
  provide: NG_VALUE_ACCESSOR,//
  useExisting: forwardRef(() => MyMonacoEditorComponent),//
  multi: true//
};
const monacoConfig: MyMonacoEditorConfig = {
  baseUrl: 'assets',
  defaultOptions: { scrollBeyondLastLine: false }
};
@Component({
  selector: 'app-my-monaco-editor',
  templateUrl: './my-monaco-editor.component.html',
  styleUrls: ['./my-monaco-editor.component.css'],
  // 自定义输入控件:2.引入依赖服务ControlValueAccessor
  providers: [CODE_EDITOR_INPUT_VALUE_ACCESSOR,
    CodeEditorEventService,
    {
      provide: MY_MONACO_EDITOR_CONFIG,
      useValue: monacoConfig
    }
  ]
})

// 自定义输入控件 <-> Monaco Edtor

// 自定义输入控件:3.1 implements ControlValueAccessor 
export class MyMonacoEditorComponent implements ControlValueAccessor, OnDestroy, OnInit {
  protected _editor: any;
  private _options: any;
  // protected _windowResizeSubscription: Subscription;

  @ViewChild('codeEditor') _editorComponent: ElementRef; //动态添加代码编辑器
  @Output() onInit;
  @Output() onChange;
  @Output() onTouched;

  @Input('options')
  set options(options: any) {
    // 默认options(this.config.defaultOptions) + 自定义options(options)
    this._options = Object.assign({}, this.config.defaultOptions, options);
    if (this._editor) {
      this._editor.dispose();
      this.initMonaco(options);
    }
  }
  get options(): any {
    return this._options;
  }
  change: number = 0;
  //在光标处插入表达式的方法
  insertWord(insertWord: string) {
    if (insertWord && insertWord != undefined && insertWord != "") {
      let p = {
        lineNumber: 1,
        column: 1
      };
      let selection;
      let range;
      if (this._editor) {
        //在选中区域中插入值
        selection = this._editor.getSelection();

        //在鼠标位置插入值
        p = this._editor.getPosition();
      }
      if (selection != null) {
        range = new monaco.Range(selection.startLineNumber, selection.startColumn, selection.endLineNumber, selection.endColumn);
      } else {
        range = new monaco.Range(p.lineNumber, p.column, p.lineNumber, p.column);
      }
      let id = { major: 1, minor: 1 };
      let op = { identifier: id, range: range, text: insertWord, forceMoveMarkers: true };
      this._editor.executeEdits("my-source", [op]);
    }
  }

  // 注入AngularMonacoEditorConfig，在创建Editor实例时设置config
  constructor(private zone: NgZone, @Inject(MY_MONACO_EDITOR_CONFIG) private config: MyMonacoEditorConfig, private codeEditorEventService: CodeEditorEventService, private eventService: ExpressionEventService) {

    // 初始化自定义事件
    const self = this;
    codeEditorEventService.eventNames.forEach((name) => {
      // 创建自定义事件，此处作用等效于: @Output() onInit = new EventEmitter<any>() 
      self[name] = new EventEmitter<any>();
      const eventPair = pick(self, name);
      codeEditorEventService.addEvent(eventPair);
    });
  }

  ngOnInit(): void {
    if (loadedMonaco) {
      // Wait until monaco editor is available
      loadPromise.then(() => {
        this.initMonaco(this.options);
      });
    } else {
      loadedMonaco = true;
      loadPromise = new Promise<void>((resolve: any) => {
        // const baseUrl = this.config.baseUrl || '/assets';
        const baseUrl = CommonUtils.convertPath('/platform/common/web/caf');
        // if (typeof((<any>window).monaco) === 'object') {
        //   resolve();
        //   return;
        // }
        const onGotAmdLoader: any = () => {
          // Load monaco
          (<any>window).require.config({ paths: { 'vs': `${baseUrl}/monaco/vs` } });
          (<any>window).require(['vs/editor/editor.main'], () => {
            this.onMonacoLoad();
            this.initMonaco(this.options);
            resolve();
          });
        };

        // Load AMD loader if necessary
        if (!(<any>window).require) {
          const loaderScript: HTMLScriptElement = document.createElement('script');
          loaderScript.type = 'text/javascript';
          loaderScript.src = `${baseUrl}/monaco/vs/loader.js`;
          loaderScript.onload = onGotAmdLoader;
          document.body.appendChild(loaderScript);
        } else {
          onGotAmdLoader();
        }
      });
    }
    //在光标处插入表达式事件订阅
    let temp = this;
    this.eventService.currentExpression().subscribe((insertexpression: any) => {
      if (temp._editor != undefined) {
        temp.insertWord(insertexpression)
      }
    });
    this.eventService.clearExpression().subscribe(() => {
      if (temp._editor != undefined) {
        temp._editor.setValue("")
      }
    });
  }

  private onMonacoLoad(): void {
    const id = "foo.json";
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      schemas: [{
        uri: "http://myserver/foo-schema.json",
        fileMatch: [id],
        schema: {
          type: "object",
          properties: {
            p1: {
              enum: ["v1", "v2"]
            },
            p2: {
              $ref: "http://myserver/bar-schema.json"
            }
          }
        }
      }, {
        uri: "http://myserver/bar-schema.json",
        fileMatch: [id],
        schema: {
          type: "object",
          properties: {
            q1: {
              enum: ["x1", "x2"]
            }
          }
        }
      }]
    })
  }

  ngOnDestroy() {
    // if (this._windowResizeSubscription) {
    //   this._windowResizeSubscription.unsubscribe();
    // }
    if (this._editor) {
      this._editor.dispose();
      this._editor = undefined;
    }
  }

  protected initMonaco(options: any): void {
    // debugger;
    // console.log("Init the custom monaco code editor.");

    // const hasModel = !!options.model;
    const hasModel = false;

    // if (hasModel) {
    //   options.model = monaco.editor.createModel(options.model.value, options.model.language, options.model.uri);
    // }

    monaco.editor.defineTheme('BlackTheme', {
      base: 'vs',
      inherit: true,
      rules: [{ background: '3E414A' }],
      colors: {
        // 相关颜色属性配置
        // 'editor.foreground': '#000000',
        'editor.background': '#F7F8FB',               //背景色
        'editor.lineHighlightBackground': '#F7F8FB',  //点击当前行颜色
        // 'editorCursor.foreground': '#8B0000',
        // 'editorLineNumber.foreground': '#000000',
        // 'editor.selectionBackground': '#88000030',
        // 'editor.inactiveSelectionBackground': '#88000015'
      }
    });
    //设置自定义主题
    monaco.editor.setTheme('BlackTheme');

    this._editor = monaco.editor.create(this._editorComponent.nativeElement, options);

    if (!hasModel) {
      this._editor.setValue(this._value);
    }

    // monaco editor -> outside component
    this._editor.onDidChangeModelContent(() => this.onChangeModelContentHandler());

    this._editor.onDidBlurEditorText(() => this.onBlurEditorTextHandler());

    // refresh layout on resize event.
    // if (this._windowResizeSubscription) {
    //   this._windowResizeSubscription.unsubscribe();
    // }
    // this._windowResizeSubscription = fromEvent(window, 'resize').subscribe(() => this._editor.layout());

    this.codeEditorEventService.fireEvent({
      eventName: CODE_EDITOR_EVENTS.onInit,
      target: this,
      editor: this._editor
    });
  }

  onChangeModelContentHandler() {
    const _value = this._editor.getValue();
    //监听编辑器值变化
    // console.log("监听：" + this.change + "==" + _value);
    // monaco editor -> outside component
    // https://github.com/JTangming/tm/issues/4 ngZone详解
    this.zone.run(() => this.value = _value);// value is not propagated to parent when executing outside zone.
    // console.log("write from the monaco:" + this._value);
  }

  onBlurEditorTextHandler() {

    this.onTouchedHandler();
  }

  private _value: string = '';

  //get accessor
  get value(): any {
    return this._value;
  };

  //set accessor including call the onchange callback
  set value(v: any) {
    if (v !== this.value) {// 注意这种写法，值得学习
      this._value = v;
    }

    this.onChangeHandler(this.value);//在属性修饰器里调用onchangeHandler方法
  }

  localEditor() {//Demo: outside component -> monaco editor
    this.writeValue('test');
  }

  // 自定义输入控件:3.2 implements ControlValueAccesso

  // outside component -> monaco editor

  //From ControlValueAccessor interface
  writeValue(value: any) {
    this.value = value || '';

    // Fix for value change while dispose in process.
    setTimeout(() => {
      if (this._editor /*&& !this.options.model*/) {
        this._editor.setValue(this._value);
        // console.log("write to the editor:" + this._value);
      }
    });
  }

  //From ControlValueAccessor interface
  registerOnChange(fn: any) {
    this.onChangeHandler(this);
  }

  //From ControlValueAccessor interface
  registerOnTouched(fn: any) {
    this.onTouchedHandler();
  }

  //ControlValueAccessor提供的事件回调
  onChangeHandler = (_: any) => { //Propagate Change to outside
    this.codeEditorEventService.fireEvent({
      eventName: CODE_EDITOR_EVENTS.onChange,
      target: this,
      data: _
    });
  };

  //ControlValueAccessor提供的事件回调
  onTouchedHandler = () => {
    this.codeEditorEventService.fireEvent({
      eventName: CODE_EDITOR_EVENTS.onTouched,
      target: this
    });
  };
}